import React, { useEffect, useRef, useState } from "react"
import { toPng } from "html-to-image"
import { viewGiftcard } from "../../../services/api/apiModule"

const ViewGiftCard = () => {
  const cardRef = useRef(null)
  const [isDownloading, setIsDownloading] = useState(false)
  const [isCopying, setIsCopying] = useState(false)
  const [isSharing, setIsSharing] = useState(false)
  const [giftCard, setGiftCard] = useState(
    {
    recipientName: "John Doe",
    senderName: "The Buff Auto Spa",
    amount: 150,
    currency: "CAD",
    code: "N2SNG7J2YY80NSLL",
    expiresAt: "2026-01-31",
    message: "Treat your ride to something special. You deserve it!",
  }
  )

  // In real use, you can pass this data via props, route params, or API
  
  const handleViewGiftCard = async(id)=>{
    try {
      const response = await viewGiftcard(id)
      setGiftCard(response)      
    } catch (error) {
      console.error(error);
      
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const id = window.location.pathname.split('/').pop()
    if (id) {
      handleViewGiftCard(id)
    }
  }, [])
  

  const formatCurrency = (value) =>
    value.toLocaleString("en-US", {
      style: "currency",
      currency: giftCard.currency || "CAD",
      minimumFractionDigits: 2,
    })

  const cardUrl =
    typeof window !== "undefined"
      ? window.location.href
      : "https://example.com/giftcard"

  const handleCopyLink = async () => {
    try {
      setIsCopying(true)
      await navigator.clipboard.writeText(cardUrl)
      setTimeout(() => setIsCopying(false), 800)
    } catch (err) {
      console.error("Failed to copy link", err)
      setIsCopying(false)
    }
  }

  const handleShare = async () => {
    try {
      setIsSharing(true)
      if (navigator.share) {
        await navigator.share({
          title: `${giftCard.brand} Gift Card`,
          text: `Here’s a ${formatCurrency(
            giftCard.amount
          )} gift card for you!`,
          url: cardUrl,
        })
      } else {
        // Fallback: copy link
        await navigator.clipboard.writeText(cardUrl)
      }
    } catch (err) {
      console.error("Share failed", err)
    } finally {
      setIsSharing(false)
    }
  }

  const handleDownloadImage = async () => {
    if (!cardRef.current) return
    setIsDownloading(true)
    try {
      toPng(cardRef.current)
        .then((dataUrl) => {
          const link = document.createElement("a")
          link.href = dataUrl
          link.download = `${giftCard.code || "gift-card"}.png`
          link.click()
        })
        .catch((err) => {
          console.error("Failed to download image", err)
        })
    } catch (err) {
      console.error("Download failed", err)
    } finally {
    setTimeout(() => setIsDownloading(false), 1000)
    }
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 flex items-center justify-center px-4 py-10 relative overflow-hidden">
      {/* Ambient background */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-40 -right-32 h-72 w-72 rounded-full bg-gradient-to-br from-[#e9c46b]/70 via-[#f4a261]/70 to-[#e76f51]/70 blur-3xl opacity-80" />
        <div className="absolute -bottom-40 -left-32 h-80 w-80 rounded-full bg-gradient-to-tr from-[#264653]/80 via-[#2a9d8f]/70 to-[#e9c46b]/60 blur-3xl opacity-80" />
        <div className="absolute inset-x-0 top-1/3 h-px bg-gradient-to-r from-transparent via-slate-700/40 to-transparent" />
      </div>

      <div className="relative w-full max-w-5xl">
        {/* Floating accents */}
        <div className="absolute -top-6 right-10 h-10 w-10 rounded-2xl bg-gradient-to-br from-[#e9c46b] to-[#f4a261] opacity-90 shadow-lg shadow-yellow-200/80 rotate-6" />
        <div className="absolute -bottom-5 left-6 h-9 w-9 rounded-3xl border border-yellow-200/80 bg-white/80 shadow-[0_18px_40px_rgba(15,23,42,0.35)] rotate-12" />

        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">
              Gift Card Viewer
            </p>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-50">
              Your digital gift card is ready 🎁
            </h1>
            <p className="mt-1 text-xs sm:text-sm text-slate-400 max-w-md">
              You can share it, copy the link, or download it as an image to
              send or print.
            </p>
          </div>
        </div>

        {/* Card + actions */}
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-10 items-stretch">
          {/* Gift Card Visual */}
          <div className="flex-1 flex items-center justify-center">
            <div
              ref={cardRef}
              className="relative w-full max-w-[620px] md:aspect-[16/9] rounded-3xl overflow-hidden border border-white/10 bg-gradient-to-br from-[#1d3557] via-[#264653] to-[#2a9d8f]  shadow-[0_30px_80px_rgba(15,23,42,0.8)]"
            >
              {/* Subtle pattern overlay */}
              <div className="absolute inset-0 opacity-20 mix-blend-soft-light">
                <div className="h-full w-full bg-[radial-gradient(circle_at_0_0,rgba(255,255,255,0.12)_0,transparent_55%),radial-gradient(circle_at_100%_0,rgba(255,255,255,0.08)_0,transparent_55%)]" />
              </div>

              {/* Ribbon */}
              <div className="absolute -left-8 top-6 rotate-[-8deg]">
                <div className="bg-gradient-to-r from-[#e9c46b] via-[#f4a261] to-[#e76f51] px-10 py-1.5 rounded-full shadow-lg shadow-slate-900/60 border border-white/30">
                  <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-900">
                    {giftCard.ShortDescription}
                  </span>
                </div>
              </div>

              {/* Brand corner tag */}
              <div className="absolute top-3 right-4 rounded-full bg-white/10 border border-white/20 px-3 py-1 backdrop-blur-sm">
                <span className="text-[11px] font-medium text-slate-100">
                  Boost Mobile Detailing
                </span>
              </div>

              {/* Content */}
              <div className="relative z-10 h-full p-6 sm:p-8 flex flex-col justify-between">
                {/* Top section */}
                <div className="flex flex-col gap-3 md:pt-4 pt-6">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex flex-col">
                      {/* <span className="text-[11px] uppercase tracking-[0.2em] text-slate-300/90">
                        To
                      </span>
                      <span className="text-lg sm:text-xl font-semibold text-slate-50 truncate">
                        {giftCard.recipientName}
                      </span> */}
                    </div>
                    <div className="text-right">
                      <span className="text-[11px] uppercase tracking-[0.17em] text-slate-300/80">
                        Value
                      </span>
                      <div className="text-xl sm:text-2xl font-bold text-[#f4a261]">
                        {formatCurrency(giftCard.currentBalance || 0)}
                      </div>
                    </div>
                  </div>

                  <div className="mt-1 flex items-center justify-between gap-4">
                    <div className="flex flex-col">
                      <span className="text-[11px] uppercase tracking-[0.17em] text-slate-300/80">
                        From
                      </span>
                      <span className="text-sm font-medium text-slate-50">
                        {giftCard.name}
                      </span>
                    </div>
                    <div className="flex flex-col text-right">
                      <span className="text-[11px] uppercase tracking-[0.17em] text-slate-300/80">
                        Expires
                      </span>
                      <span className="text-xs font-medium text-slate-100">
                        {giftCard.expiryDate}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Middle message */}
                <div className="mt-4 flex-1 flex items-center">
                  <div className="rounded-2xl bg-black/20 border border-white/10 px-4 py-3 max-h-28 overflow-hidden">
                    <p className="text-xs sm:text-sm text-slate-100/90 line-clamp-3">
                      {giftCard.note}
                    </p>
                  </div>
                </div>

                {/* Bottom code + strip */}
                <div className="mt-4 flex flex-col gap-3">
                  {/* Decorative strip */}
                  <div className="h-8 w-full rounded-2xl bg-[repeating-linear-gradient(135deg,rgba(250,250,250,0.1)_0,rgba(250,250,250,0.1)_8px,transparent_8px,transparent_16px)] border border-white/15 flex items-center px-4 justify-between">
                    <span className="text-[10px] uppercase tracking-[0.21em] text-slate-200/80">
                      Non-Refundable • One-Time Use • Online Booking • Balance Based
                    </span>
                  </div>

                  {/* Code */}
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex flex-col">
                      <span className="text-[10px] uppercase tracking-[0.21em] text-slate-300/90">
                        Gift Card Code
                      </span>
                      <div className="mt-1 inline-flex items-center gap-2 rounded-xl bg-black/30 px-3 py-1.5 border border-white/15">
                        <span className="text-[13px] font-mono tracking-[0.25em] text-slate-50">
                          {giftCard.giftCardCode}
                        </span>
                      </div>
                    </div>
                    <div className="hidden sm:flex flex-col items-end text-[10px] text-slate-300/80">
                      <span>Present this code at checkout</span>
                      <span>Valid for detailing services only</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="w-full lg:w-64 flex flex-col gap-4">
            <div className="rounded-3xl border border-slate-800 bg-slate-900/70 shadow-xl shadow-black/50 px-5 py-4 backdrop-blur-xl">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">
                Share Gift Card
              </p>
              <p className="text-xs text-slate-400 mb-4">
                Share this gift card with your guest, or download it as an image
                to send via email or print.
              </p>

              <div className="space-y-2">
                <button
                  type="button"
                  onClick={handleShare}
                  disabled={isSharing}
                  className={`w-full inline-flex items-center justify-center rounded-2xl px-3 py-2.5 text-xs font-semibold transition ${
                    isSharing
                      ? "cursor-wait bg-slate-700 text-slate-300"
                      : "bg-gradient-to-r from-[#e9c46b] via-[#f4a261] to-[#e76f51] text-slate-900 shadow-lg shadow-yellow-500/40 hover:brightness-110"
                  }`}
                >
                  {isSharing ? "Sharing..." : "Share gift card"}
                </button>

                <button
                  type="button"
                  onClick={handleCopyLink}
                  disabled={isCopying}
                  className={`w-full inline-flex items-center justify-center rounded-2xl px-3 py-2.5 text-xs font-semibold transition border ${
                    isCopying
                      ? "cursor-wait border-slate-600 bg-slate-800 text-slate-300"
                      : "border-slate-700 bg-slate-900 text-slate-100 hover:border-slate-500"
                  }`}
                >
                  {isCopying ? "Link copied!" : "Copy gift card link"}
                </button>

                <button
                  type="button"
                  onClick={handleDownloadImage}
                  disabled={isDownloading}
                  className={`w-full inline-flex items-center justify-center rounded-2xl px-3 py-2.5 text-xs font-semibold transition border ${
                    isDownloading
                      ? "cursor-wait border-slate-600 bg-slate-800 text-slate-300"
                      : "border-slate-700 bg-slate-900 text-slate-100 hover:border-slate-500"
                  }`}
                >
                  {isDownloading ? "Preparing image..." : "Download as image"}
                </button>
              </div>
            </div>

            <div className="rounded-3xl border border-slate-800 bg-slate-900/60 px-5 py-4 text-[11px] text-slate-400 backdrop-blur-xl">
              <p className="font-semibold text-slate-200 mb-1">How it works</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Guest uses the code during booking or checkout.</li>
                <li>Balance is applied up to the card value.</li>
                <li>Valid until the expiration date shown.</li>
                <li>Can be used for eligible services only.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ViewGiftCard
